/**
 * Trimming policy for the persisted Apollo snapshot.
 *
 * Score entities are ~97% of cache volume (one scored session is roughly 0.5MB, and
 * the staff cache is never garbage collected), and every persist re-serializes the
 * whole cache on the main thread. Synced scores are re-fetchable and unsynced ones
 * replay from the offline action buffer, so only the sessions a user is actively
 * scoring need to be kept.
 *
 * Bounding happens in memory (evictScoresOutsideRecentSessions) so the serialization
 * every persist pays stays cheap; trimPersistedScores is the snapshot-side backstop.
 */
import { ApolloCache } from '@apollo/client';
import _ from 'lodash';

import { store } from './storage';

const RECENT_SCORED_SESSIONS_REF = 'max_RecentScoredSessions';
const MAX_RECENT_SCORED_SESSIONS = 5;
const SCORE_ENTITY_PREFIX = 'Score:';

let recentScoredSessionIds: string[] = [];

// Seeded once at client construction so the mapper stays synchronous-cheap per persist.
export const loadRecentScoredSessions = async (): Promise<void> => {
  try {
    const stored = await store.get(RECENT_SCORED_SESSIONS_REF);
    recentScoredSessionIds = stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('loadRecentScoredSessions failed:', error);
    recentScoredSessionIds = [];
  }
};

const sessionIdFromStoreFieldName = (storeFieldName: string): string | undefined =>
  (/"assessmentSessionId":"([^"]+)"/.exec(storeFieldName) || [])[1];

// Evicting the root field orphans that session's Score entities; gc() then reclaims them.
export const evictScoresOutsideRecentSessions = (cache: ApolloCache<any>): void => {
  const keep = new Set(recentScoredSessionIds);
  let evicted = false;

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      evaluationScores(existing: any, { storeFieldName, DELETE }: any) {
        const sessionId = sessionIdFromStoreFieldName(storeFieldName);
        if (!sessionId || keep.has(sessionId)) return existing;

        evicted = true;
        return DELETE;
      },
    },
  });

  if (evicted) cache.gc();
};

export const noteScoredSession = async (cache: ApolloCache<any>, assessmentSessionId?: string): Promise<void> => {
  if (!assessmentSessionId) return;

  if (recentScoredSessionIds[0] !== assessmentSessionId) {
    recentScoredSessionIds = [assessmentSessionId, ..._.without(recentScoredSessionIds, assessmentSessionId)]
      .slice(0, MAX_RECENT_SCORED_SESSIONS);

    try {
      await store.set(RECENT_SCORED_SESSIONS_REF, JSON.stringify(recentScoredSessionIds));
    } catch (error) {
      console.error('noteScoredSession failed to persist:', error);
    }
  }

  // Runs even when the list is unchanged so a cache carrying pre-existing bloat
  // (scores cached before this tracking existed) still gets swept.
  try {
    evictScoresOutsideRecentSessions(cache);
  } catch (error) {
    console.error('noteScoredSession failed to evict older scores:', error);
  }
};

export const trimPersistedScores = async (data: any): Promise<any> => {
  if (typeof data !== 'string') return data;

  let snapshot: Record<string, any>;
  try {
    snapshot = JSON.parse(data);
  } catch (error) {
    console.error('trimPersistedScores could not parse the cache snapshot:', error);
    return data;
  }

  const keep = new Set(recentScoredSessionIds);
  const dropped = new Set<string>();

  Object.keys(snapshot).forEach((key) => {
    if (!key.startsWith(SCORE_ENTITY_PREFIX)) return;

    const sessionId = snapshot[key] && snapshot[key].assessmentSessionId;
    // A score with no session (calibration) can't be classified — keep it.
    if (!sessionId || keep.has(sessionId)) return;

    dropped.add(key);
    delete snapshot[key];
  });

  if (!dropped.size) return data;

  // Drop a list holding a dropped ref outright: a shortened list reads as complete-but-wrong
  // (an empty score list renders "no scores") instead of missing, so nothing would refetch.
  Object.keys(snapshot).forEach((entityKey) => {
    const entity = snapshot[entityKey];
    if (!entity || typeof entity !== 'object') return;

    Object.keys(entity).forEach((field) => {
      const value = entity[field];
      if (Array.isArray(value) && value.some((item: any) => item && dropped.has(item.__ref))) delete entity[field];
    });
  });

  return JSON.stringify(snapshot);
};
